import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import LeaderboardTable from "./LeaderboardTable";
import { API_BASE } from "../../config/api";

const MODES = {
  NORMAL: "normal",
  HARD: "hard",
  DAILY: "daily",
};

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const modeRaw = searchParams.get("mode");
  const mode = [MODES.NORMAL, MODES.HARD, MODES.DAILY].includes(modeRaw)
    ? modeRaw
    : MODES.NORMAL;

  const [rows, setRows] = useState([]);
  const [dailyDateStr, setDailyDateStr] = useState(
    () => new Date().toISOString().split("T")[0]
  );

  const setMode = (nextMode) => {
    setSearchParams({ mode: nextMode });
  };

  const loadNormalHard = useCallback(async (gameMode) => {
    try {
      const endpoint =
        gameMode === MODES.HARD
          ? `${API_BASE}/api/hardGame/leaderboard`
          : `${API_BASE}/api/game/leaderboard`;
      const res = await axios.get(endpoint);
      const data = res.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
      setRows([]);
    }
  }, []);

  const loadDaily = useCallback(async (dateIsoDay) => {
    try {
      const res = await axios.get(`${API_BASE}/api/dailyLeaderboard/${dateIsoDay}`);
      const players = res.data?.players ?? [];
      setRows(Array.isArray(players) ? players : []);
    } catch (err) {
      console.error("Error fetching daily leaderboard:", err);
      setRows([]);
    }
  }, []);

  useEffect(() => {
    if (mode === MODES.DAILY) {
      loadDaily(dailyDateStr);
    } else {
      loadNormalHard(mode);
    }
  }, [dailyDateStr, loadDaily, loadNormalHard, mode]);

  const titles = {
    [MODES.NORMAL]: "Normal leaderboard",
    [MODES.HARD]: "Hard leaderboard",
    [MODES.DAILY]: `Daily — ${dailyDateStr}`,
  };

  return (
    <div className="mv-page">
      <div className="mx-auto w-full max-w-6xl pb-16">
        <div className="mv-card px-6 py-8 md:px-10 md:py-10">
          <div className="text-center md:text-left">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-cinema-accent-muted">
              Rankings
            </p>
            <h1 className="mv-heading text-3xl md:text-4xl">Leaderboards</h1>
            <p className="mx-auto mt-3 max-w-xl text-slate-400 md:mx-0">
              Normal, hard, and daily top scores. Switch modes to compare runs.
            </p>
          </div>

          <div
            className="mt-8 flex flex-wrap justify-center gap-2 md:justify-start"
            role="tablist"
            aria-label="Leaderboard mode"
          >
            {[MODES.NORMAL, MODES.HARD, MODES.DAILY].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                className={`mv-btn rounded-lg px-4 py-2 text-sm capitalize ${
                  mode === key
                    ? "bg-cinema-accent text-cinema-950 shadow-glow"
                    : "border border-slate-600/80 bg-cinema-850/50 text-slate-300 hover:border-slate-500 hover:bg-cinema-800/60"
                }`}
              >
                {key}
              </button>
            ))}
          </div>

          {mode === MODES.DAILY && (
            <div className="mt-8 flex flex-col items-center gap-2 sm:flex-row sm:justify-center md:justify-start">
              <label htmlFor="daily-date" className="mv-label mb-0 sm:mr-3">
                Date
              </label>
              <input
                id="daily-date"
                type="date"
                value={dailyDateStr}
                onChange={(e) => setDailyDateStr(e.target.value)}
                className="mv-input max-w-xs sm:max-w-none"
              />
            </div>
          )}

          <LeaderboardTable title={titles[mode]} leaderboard={rows} />

          <div className="mt-10 flex flex-wrap justify-center gap-3 border-t border-slate-700/50 pt-8 md:justify-start">
            <button
              type="button"
              onClick={() => navigate("/game")}
              className="mv-btn-primary px-8"
            >
              Back to game
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mv-btn-secondary px-8"
            >
              Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
